import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import HeroCollage from "@/components/HeroCollage";
import {
  CalendarDays, Users, Sparkles, FileSpreadsheet, FileText, Bell,
  ShieldCheck, Layers, Smartphone, ListChecks, BarChart3, CheckCircle2,
} from "lucide-react";

const BENEFITS = [
  { icon: CalendarDays, label: "Build schedules faster", body: "Visual weekly roster with bulk shift apply, copy-from-last-week, and shift templates." },
  { icon: ListChecks,   label: "Manage leave requests", body: "Approvals workflow, half-day support, automatic capacity blocking, bank-holiday awareness." },
  { icon: Sparkles,     label: "Track employee skills", body: "Tag your people with the skills they bring and surface them at scheduling time." },
  { icon: Bell,         label: "Publish rosters instantly", body: "One click sends each employee their week by email. Draft saves never spam." },
  { icon: FileSpreadsheet, label: "Export to Excel and PDF", body: "Share-ready workbooks and landscape PDFs. Frozen panes, freeze rows, branding." },
  { icon: Smartphone,   label: "Mobile-friendly access", body: "Drawer navigation, swipe roster, employee read-only roster - works on any phone." },
];

const ROADMAP = [
  "Salesforce connector",
  "API integrations",
  "Generic import API",
  "Undo import and batch rollback",
  "Period-comparison sparklines",
  "Multi-site expansion",
  "Advanced automation expansion",
];

/** A minimal "shadow card" mimicking a roster cell - used as a product preview
 *  on the home page when we don't have a screenshot ready. Real screenshots
 *  replace these once captured. */
function RosterPreview() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-[0_20px_60px_-20px_rgba(31,42,68,0.25)] overflow-hidden max-w-full"
         data-testid="home-roster-preview">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-[#F6F9FE]">
        <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]"/>
        <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]"/>
        <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]"/>
        <span className="ml-3 text-xs text-[#4B5773]">NextRota - Week of 8 June 2026</span>
      </div>
      <div className="p-4 overflow-x-auto">
        <table className="text-xs w-full min-w-[640px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[#4B5773]">
              <th className="text-left py-2 pr-3">Employee</th>
              {["Mon 8","Tue 9","Wed 10","Thu 11","Fri 12"].map(d => (
                <th key={d} className="text-center px-2 py-2">{d}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              ["Devon S.", ["09-17 Outbound","09-17 Inbound","09-17 Outbound","09-17 Training","09-17 Outbound"]],
              ["Riley C.", ["10-18 Inbound","10-18 Inbound","10-18 Leave","-","10-18 Inbound"]],
              ["Morgan R.", ["-","13-21 Helpdesk","13-21 Helpdesk","13-21 Helpdesk","-"]],
              ["Jordan B.", ["09-17 Outbound","09-17 Outbound","09-17 Outbound","09-17 Outbound","09-17 Outbound"]],
            ].map(([name, cells]) => (
              <tr key={name} className="hover:bg-[#F6F9FE]">
                <td className="py-2 pr-3 font-medium text-[#1F2A44]">{name}</td>
                {cells.map((c, i) => (
                  <td key={i} className="py-2 px-2 text-center">
                    {c === "-" ? <span className="text-slate-300">-</span>
                     : c.includes("Leave") ? <span className="inline-block px-1.5 py-0.5 rounded-full bg-[#FFE9DC] text-[#9A3412]">Leave</span>
                     : <span className="text-[#1F2A44]">{c}</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
      <PublicNav />

      {/* HERO */}
      <section className="bg-gradient-to-b from-[#EEF3FB] to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-16">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7 min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#DBE7FE] text-[#1D4ED8] px-3 py-1 text-xs font-semibold mb-5"
                   data-testid="home-founding-pill">
                <Sparkles className="h-3.5 w-3.5"/> First 10 founding companies, 3 months free
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0B1A36] leading-tight">
                The workforce command centre for modern teams.
              </h1>
              <p className="mt-5 text-lg text-[#4B5773] max-w-xl">
                Create smarter rotas, protect coverage, track performance and automate routine rota work with AI-assisted intelligence. Managers stay in control.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/start" data-testid="home-hero-start-btn"
                  className="inline-flex items-center px-5 py-3 rounded-md bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] shadow-sm">
                  Start 14-day free trial
                </Link>
                <a href="mailto:hello@nextrota.com?subject=NextRota%20demo%20request"
                  data-testid="home-hero-demo-btn"
                  className="inline-flex items-center px-5 py-3 rounded-md border border-slate-200 text-[#1F2A44] font-semibold hover:bg-slate-50">
                  Book a Demo
                </a>
              </div>
              <p className="mt-4 text-xs text-[#4B5773]">14-day free trial, no credit card required. Cancel anytime.</p>
            </div>
            <div className="md:col-span-5 min-w-0"><HeroCollage /></div>
          </div>

          {/* Phase 2Z.w-2 - Outcome-focused mini-section. Concrete benefits,
              not feature names. Frames NextRota as a time-saving command centre. */}
          <div className="mt-16 grid sm:grid-cols-3 gap-5" data-testid="home-outcome-strip">
            <div className="rounded-xl border border-slate-200 bg-white p-5"
              data-testid="home-outcome-time">
              <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">Save manager time</div>
              <div className="font-display text-lg text-[#0B1A36] mt-1">Less rota admin every week</div>
              <p className="text-sm text-[#4B5773] mt-2">
                Set Rota Style once. Let the Autopilot module prepare drafts, approve on schedule and publish when configured.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5"
              data-testid="home-outcome-coverage">
              <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">Smart coverage protection</div>
              <div className="font-display text-lg text-[#0B1A36] mt-1">Coverage Guard catches gaps</div>
              <p className="text-sm text-[#4B5773] mt-2">
                Every rota change is checked against your minimum cover rules. Warned before any edit leaves a gap.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5"
              data-testid="home-outcome-performance">
              <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">Performance-linked planning</div>
              <div className="font-display text-lg text-[#0B1A36] mt-1">Connect rota and results</div>
              <p className="text-sm text-[#4B5773] mt-2">
                Performance Focus compares activity results against the work you assigned, fairly adjusted for leave.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <div className="eyebrow text-[#4B5773]">Why NextRota</div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#0B1A36] mt-2">Everything you need to run a great roster</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map(({ icon: Icon, label, body }) => (
            <div key={label} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="h-10 w-10 rounded-lg bg-[#DBE7FE] text-[#1D4ED8] grid place-items-center mb-3">
                <Icon className="h-5 w-5"/>
              </div>
              <div className="font-display text-lg text-[#0B1A36]">{label}</div>
              <p className="text-sm text-[#4B5773] mt-1.5">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#F6F9FE] py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <div className="eyebrow text-[#4B5773]">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0B1A36] mt-2">Up and running in three steps</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ["1", "Create your teams and employees", "Add the people you schedule. Set working patterns once, edit anytime."],
              ["2", "Build and publish schedules", "Drag-fast cell editor, shift templates, bulk apply across days and people."],
              ["3", "Keep everyone informed automatically", "Publish once - the right employees get a clean, branded email."],
            ].map(([n, title, body]) => (
              <div key={n} className="rounded-xl bg-white border border-slate-200 p-6">
                <div className="h-9 w-9 rounded-full bg-[#0B1A36] text-white grid place-items-center font-display text-base">{n}</div>
                <div className="mt-3 font-display text-lg text-[#0B1A36]">{title}</div>
                <p className="text-sm text-[#4B5773] mt-1.5">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDING CUSTOMER */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="rounded-2xl bg-[#0B1A36] text-white p-8 sm:p-12 relative overflow-hidden" data-testid="home-founding-section">
          <div className="grid md:grid-cols-12 gap-8 items-center relative z-10">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">
                <Sparkles className="h-3.5 w-3.5"/> Founding Customer Programme
              </div>
              <h2 className="font-display text-3xl sm:text-4xl mt-3">Be one of the first 10 companies to shape NextRota.</h2>
              <ul className="mt-5 space-y-2 text-sm text-white/85">
                {["3 months free founder access","No credit card required","Full access to every feature","Help shape the future of NextRota","Limited to the first 10 founding companies"].map(t => (
                  <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#28C840]"/>{t}</li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-white/60">Not a founder? Every new company still gets a 14-day free trial, no credit card required.</p>
            </div>
            <div className="md:col-span-5 flex md:justify-end">
              <Link to="/start?founder=1" data-testid="home-founding-cta"
                className="inline-flex items-center px-6 py-3 rounded-md bg-white text-[#0B1A36] font-semibold hover:bg-slate-100">
                Claim your founding spot
              </Link>
            </div>
          </div>
          <div className="pointer-events-none absolute -top-16 -right-16 h-72 w-72 rounded-full bg-[#2563EB]/30 blur-3xl"/>
        </div>
      </section>

      {/* FUTURE ROADMAP */}
      <section className="bg-[#F6F9FE] py-16" data-testid="home-roadmap-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <div className="inline-block rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 mb-3">Coming soon</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0B1A36]">Future capabilities we're building</h2>
            <p className="text-sm text-[#4B5773] mt-2 max-w-2xl mx-auto">
              These are on the NextRota roadmap and not yet available. Founding customers help us prioritise what ships first.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {ROADMAP.map(item => (
              <div key={item} className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-[#1F2A44] flex items-start gap-2">
                <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Soon</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
        <h2 className="font-display text-3xl sm:text-4xl text-[#0B1A36]">Ready to run a better roster?</h2>
        <p className="text-base text-[#4B5773] mt-3">Start your 14-day free trial in under a minute. First 10 founding companies get 3 months free.</p>
        <Link to="/start" data-testid="home-bottom-cta"
          className="mt-6 inline-flex items-center px-6 py-3 rounded-md bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8]">
          Start Free Trial
        </Link>
      </section>

      <Footer />
    </div>
  );
}
